import { useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { Form, Formik } from "formik";
import { Button, Container, FormControl, Grid2 as Grid, InputAdornment, InputLabel, MenuItem, OutlinedTextFieldProps, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import { Organization } from "@/types";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { updateOrganization } from "@/features/organizations/slice";
import { OrganizationContext } from "@/features/organizations/components/OrganizationLayout";
import { INITIAL_LOCATION } from "@/components/map/Map";
import { industries, Industry } from "@/constants/industries";
import * as Yup from "yup";
import PageHeader from "@/components/PageHeader";
import InputMask from "react-input-mask";
import useSnackbar from "@/hooks/useSnackbar";
import { useAccessControl, withAccessControl } from "@/features/access-control";
import { sanitizePhone } from "@/utils/phones";
import OrganizationBranches from "@/pages/organization-details/components/OrganizationBranches";
import { HugeiconsIcon } from "@hugeicons/react";
import { Facebook01Icon, InstagramIcon, Mail01Icon, TelephoneIcon, TiktokIcon, WhatsappIcon } from "@hugeicons/core-free-icons";
import OrganizationMap from "./components/OrganizationMap";
import OrganizationVisualIdentity from "./components/OrganizationVisualdentity";

type FormValues = Pick<
  Organization,
  | "id"
  | "title"
  | "slogan"
  | "industry"
  | "email"
  | "address"
  | "latitude"
  | "longitude"
  | "website"
  | "domain"
  | "googlePlaceId"
  | "phone"
  | "whatsapp"
  | "facebook"
  | "instagram"
  | "tiktok"
  | "mainBranch"
  | "primaryColor"
  | "logo"
>;

const validationSchema = Yup.object().shape({
  title: Yup.string().strict(true).min(4, "Muy Corto!").max(50, "Muy Largo!").required("Campo requerido"),
  slogan: Yup.string(),
  domain: Yup.string(),
  googlePlaceId: Yup.string(),
  address: Yup.string().strict(true).required("Ingresa tu dirección, para que tus clientes te encuentren"),
  phone: Yup.string().min(8, "Muy Corto!").max(9, "Muy Largo!").required("Campo requerido"),
  whatsapp: Yup.string().min(8, "Muy Corto!").max(9, "Muy Largo!").required("Campo requerido"),
  facebook: Yup.string(),
  instagram: Yup.string(),
  tiktok: Yup.string(),
  email: Yup.string().strict(true).email("Correo inválido").required("Campo requerido"),
  primaryColor: Yup.string(),
  logo: Yup.string(),
});

const OrganizationDetails = () => {
  const dispatch = useAppDispatch();
  const container = useAppSelector((state) => state.ui?.container);
  const [openSnackbar] = useSnackbar();
  const { hasAccess } = useAccessControl();
  const { currentOrganization } = useOutletContext<OrganizationContext>();

  const sharedInputProps = useMemo(() => {
    return {
      size: "small",
      fullWidth: true,
      margin: "normal",
      disabled: !hasAccess("update.organization"),
    } as Pick<OutlinedTextFieldProps, "size" | "fullWidth" | "disabled" | "margin">;
  }, [hasAccess]);

  const handleSubmit = async (values: FormValues) => {
    try {
      const sanitizedValues = {
        ...values,
        phone: Number(sanitizePhone(String(values.phone))),
        whatsapp: Number(sanitizePhone(String(values.whatsapp))),
      };
      await dispatch(updateOrganization(sanitizedValues));
      openSnackbar("Datos actualizados", "success");
    } catch (error) {
      console.error(error);
      openSnackbar("Error al actualizar los datos", "error");
    }
  }


  if (!currentOrganization) return null;

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={
        {
          id: currentOrganization?.id,
          title: currentOrganization?.title ?? "",
          slogan: currentOrganization?.slogan ?? "",
          industry: currentOrganization?.industry ?? "",
          website: currentOrganization?.website ?? "",
          domain: currentOrganization?.domain ?? "",
          googlePlaceId: currentOrganization?.googlePlaceId ?? "",
          email: currentOrganization?.email ?? "",
          address: currentOrganization?.address ?? "",
          phone: currentOrganization?.phone ?? "",
          whatsapp: currentOrganization?.whatsapp ?? "",
          facebook: currentOrganization?.facebook ?? "",
          instagram: currentOrganization?.instagram ?? "",
          tiktok: currentOrganization?.tiktok ?? "",
          latitude: currentOrganization?.latitude ?? INITIAL_LOCATION.lat,
          longitude: currentOrganization?.longitude ?? INITIAL_LOCATION.lng,
          mainBranch: currentOrganization?.mainBranch ?? "",
          primaryColor: currentOrganization?.primaryColor ?? "",
          logo: currentOrganization?.logo ?? "",
        }
      }
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, values, touched, errors, handleChange, handleBlur, setFieldValue, setFieldTouched }) => (
        <Form>

          <Stack spacing={3}>
            <PageHeader
              title="Datos del comercio"
              action={
                hasAccess("update.organization") ? (
                  <Button size="large" color="success" type="submit" fullWidth={false} onClick={() => handleSubmit()}>
                    Guardar
                  </Button>
                ) : undefined
              }
            />
            <Stack>
              <Container
                maxWidth={container === 'fixed' ? 'lg' : false}
                sx={{
                  height: '100%',
                }}
              >
                <Stack spacing={2} height={'100%'}>
                  <Grid container direction={"row"} spacing={3}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Stack gap={2}>
                        <Paper sx={{ padding: 2 }}>
                          <Stack gap={2}>
                            <Typography variant="h4">Generales</Typography>
                            <Stack>
                              <Stack direction={"row"} spacing={2}>
                                <TextField
                                  label="Título"
                                  name="title"
                                  value={values.title}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={!!touched.title && !!errors?.title}
                                  helperText={touched.title && errors?.title}
                                  {...sharedInputProps}
                                />

                                {hasAccess("update.industry") ? (
                                  <FormControl fullWidth {...sharedInputProps}>
                                    <InputLabel id="industry-id">Industria</InputLabel>
                                    <Select
                                      labelId="industry-id"
                                      label="Industria"
                                      name="industry"
                                      value={values.industry ?? ""}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={!!touched.industry && !!errors?.industry}
                                    >
                                      {Object.values(industries).map((industry: Industry) => (
                                        <MenuItem key={industry.id} value={industry.id}>
                                          {industry.locale.es}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : null}
                              </Stack>
                              <TextField
                                label="Eslogan"
                                name="slogan"
                                value={values.slogan}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!touched.slogan && !!errors?.slogan}
                                helperText={touched.slogan && errors?.slogan}
                                {...sharedInputProps}
                              />
                            </Stack>
                          </Stack>
                        </Paper>
                        <Paper sx={{ padding: 2 }}>
                          <Stack gap={2}>
                            <Typography variant="h4">Contacto</Typography>
                            <Stack>
                              <Stack
                                direction={"row"}
                                sx={{
                                  gap: "16px",
                                }}
                              >

                                <InputMask
                                  mask="9999-9999"
                                  maskChar={null}
                                  placeholder="0000-0000"
                                  value={values.whatsapp}
                                  onChange={(e) => setFieldValue("whatsapp", e.target.value)}
                                  onBlur={() => setFieldTouched("whatsapp", true)}
                                  disabled={sharedInputProps.disabled}
                                >
                                  {() => (
                                    <TextField
                                      label="Whatsapp"
                                      name="whatsapp"
                                      error={!!touched.whatsapp && !!errors.whatsapp}
                                      helperText={touched.whatsapp && errors.whatsapp}
                                      {...sharedInputProps}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <HugeiconsIcon icon={WhatsappIcon} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                </InputMask>
                                <InputMask
                                  mask="9999-9999"
                                  maskChar={null}
                                  placeholder="0000-0000"
                                  value={values.phone}
                                  onChange={(e) => setFieldValue("phone", e.target.value)}
                                  onBlur={() => setFieldTouched("phone", true)}
                                  disabled={sharedInputProps.disabled}
                                >
                                  {() => (
                                    <TextField
                                      label="Teléfono"
                                      name="phone"
                                      error={!!touched.phone && !!errors.phone}
                                      helperText={touched.phone && errors.phone}
                                      {...sharedInputProps}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <HugeiconsIcon icon={TelephoneIcon} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                </InputMask>

                              </Stack>

                              <TextField
                                label="Email"
                                type="email"
                                slotProps={{
                                  input: {
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <HugeiconsIcon icon={Mail01Icon} />
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                                name="email"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!touched.email && !!errors?.email}
                                helperText={touched.email && errors?.email}
                                {...sharedInputProps}
                              />
                              <TextField
                                label="Sitio web"
                                name="domain"
                                value={values.domain}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!touched.domain && !!errors?.domain}
                                helperText={touched.domain && errors?.domain}
                                {...sharedInputProps}
                              />
                              <TextField
                                label="Google Place ID (Google Reviews)"
                                name="googlePlaceId"
                                placeholder="Ej: ChIJN1t_tDeuEmsRUsoyG83frY4"
                                value={values.googlePlaceId ?? ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!touched.googlePlaceId && !!errors?.googlePlaceId}
                                helperText={
                                  (touched.googlePlaceId && errors?.googlePlaceId) ||
                                  "ID de tu negocio en Google Maps para vincular reseñas con tus encuestas"
                                }
                                {...sharedInputProps}
                              />


                              <OrganizationMap
                                latitude={values?.latitude}
                                longitude={values?.longitude}
                                onChange={(latitude, longitude) => {
                                  if (hasAccess("update.organization")) {
                                    setFieldValue("latitude", latitude);
                                    setFieldValue("longitude", longitude);
                                  }
                                }}
                              />

                              <TextField
                                label="Dirección"
                                multiline
                                rows={3}
                                name="address"
                                value={values.address}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!touched.address && !!errors?.address}
                                helperText={touched.address && errors?.address}
                                {...sharedInputProps}
                              />
                            </Stack>
                          </Stack>
                        </Paper>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }} mb={3}>
                      <Stack gap={2}>
                        {currentOrganization?.id ? (
                          <Paper sx={{ padding: 2 }}>
                            <Stack gap={2}>
                              <Typography variant="h4">Sucursales</Typography>
                              <Stack>
                                <OrganizationBranches
                                  name="mainBranch"
                                  organizationId={currentOrganization.id}
                                  value={values.mainBranch}
                                  onChange={handleChange}
                                />
                              </Stack>
                            </Stack>
                          </Paper>
                        ) : null}
                        {hasAccess("view.website") ? (
                          <>
                            <OrganizationVisualIdentity
                              value={{
                                primaryColor: values.primaryColor ?? "",
                                logo: values.logo ?? "",
                              }}
                              onChange={(field, value) => {
                                setFieldValue(field, value);
                              }}
                            />
                          </>

                        ) : null}
                        <Paper sx={{ padding: 2 }}>
                          <Stack gap={2}>
                            <Typography variant="h4">Redes Sociales</Typography>
                            <Stack>
                              <TextField
                                label="Facebook"
                                type="url"
                                slotProps={{
                                  input: {
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <HugeiconsIcon icon={Facebook01Icon} />
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                                name="facebook"
                                value={values.facebook}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!touched.facebook && !!errors?.facebook}
                                helperText={touched.facebook && errors?.facebook}
                                {...sharedInputProps}
                              />
                              <TextField
                                label="Instagram"
                                type="url"
                                slotProps={{
                                  input: {
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <HugeiconsIcon icon={InstagramIcon} />
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                                name="instagram"
                                value={values.instagram}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!touched.instagram && !!errors?.instagram}
                                helperText={touched.instagram && errors?.instagram}
                                {...sharedInputProps}
                              />
                              <TextField
                                label="TikTok"
                                type="url"
                                slotProps={{
                                  input: {
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <HugeiconsIcon icon={TiktokIcon} />
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                                name="tiktok"
                                value={values.tiktok}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!touched.tiktok && !!errors?.tiktok}
                                helperText={touched.tiktok && errors?.tiktok}
                                {...sharedInputProps}
                              />
                            </Stack>
                          </Stack>
                        </Paper>
                      </Stack>
                    </Grid>
                  </Grid>
                </Stack>
              </Container>
            </Stack>

          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default withAccessControl(OrganizationDetails, "view.organization");
